// /admin/usuarios/:id — one INHERTA staff account's detail page: identity,
// active sessions and the same management actions the Equipo table's row
// menu offers (Cambiar rol, restablecer contraseña, restablecer MFA,
// desactivar/reactivar, eliminar). Every action reuses the SAME shared modal
// components the table uses (users/*.tsx) — no duplicated implementations,
// so behavior/copy/rails stay identical on both surfaces. Data via
// getUserDetail()/adminListUserSessions() in users-data.ts, wired to
// GET /admin/users/:id and GET /admin/users/:id/sessions.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { DeleteUserModal } from './users/DeleteUserModal.js'
import { ReasonModal, type ReasonModalTarget } from './users/ReasonModal.js'
import { RoleModal, type RoleModalTarget } from './users/RoleModal.js'
import { SecondaryEmailModal, type SecondaryEmailModalTarget } from './users/SecondaryEmailModal.js'
import {
  absoluteDateTimeEs,
  adminListUserSessions,
  getUserDetail,
  grantableRoles,
  mfaEnrolledLabel,
  reactivateUser,
  relativeDateEs,
  revokeUserSessions,
  ROLE_BADGE,
  sendPasswordReset,
  type UserSession,
} from './users-data.js'
import { useSession } from '../../auth/session.js'
import { useAuthStore } from '../../auth/store.js'
import {
  Avatar,
  Button,
  Card,
  Chip,
  DataTable,
  type DataTableColumn,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { useAvatar } from '../../lib/useAvatar.js'

/** Label/value row for the Información card — same shape as TenantDetail's
 *  local MetaRow, re-implemented here (a two-line presentational row is not
 *  the kind of duplication the modal-extraction rule targets). */
function InfoRow({ k, children }: { k: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b border-line py-2.5 last:border-b-0">
      <span className="text-[12px] text-text-2">{k}</span>
      <span className="text-[12.5px] font-medium text-text">{children}</span>
    </div>
  )
}

const sessionColumns: DataTableColumn<UserSession>[] = [
  {
    key: 'device',
    label: 'Dispositivo',
    render: (s) => <span className="text-text">{s.device}</span>,
  },
  {
    key: 'location',
    label: 'Ubicación',
    render: (s) => <span className="text-text-2">{s.location}</span>,
  },
  {
    key: 'lastActive',
    label: 'Última actividad',
    align: 'right',
    render: (s) => <span className="tabular-nums text-text-2">{s.lastActive}</span>,
  },
]

export function UserDetailPage(): JSX.Element {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const session = useSession()
  const selfId = useAuthStore((s) => s.user?.id)
  const roles = grantableRoles(session?.platformRole)

  const { data: detail, isLoading } = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => getUserDetail(id),
    enabled: id !== '',
  })
  const { data: sessionsList, isLoading: sessionsLoading } = useQuery({
    queryKey: ['admin', 'user-sessions', id],
    queryFn: () => adminListUserSessions(id),
    enabled: id !== '',
  })

  const [busy, setBusy] = useState(false)
  const [roleTarget, setRoleTarget] = useState<RoleModalTarget | null>(null)
  const [reasonTarget, setReasonTarget] = useState<ReasonModalTarget | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; email: string } | null>(null)
  const [secondaryTarget, setSecondaryTarget] = useState<SecondaryEmailModalTarget | null>(null)

  const avatarSrc = useAvatar(detail?.id, detail?.has_avatar)

  async function refreshDetail(): Promise<void> {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['admin', 'user-detail', id] }),
      qc.invalidateQueries({ queryKey: ['admin', 'org-users'] }),
    ])
  }

  async function afterDelete(): Promise<void> {
    // Both surfaces that could still be showing this (now-gone) account: the
    // Equipo table (org-users) AND the Directorio tab, since a deleted user
    // may have appeared there too (any user, staff or client). Invalidate
    // BEFORE navigating back so /admin/usuarios never mounts onto stale
    // cached data.
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['admin', 'org-users'] }),
      qc.invalidateQueries({ queryKey: ['admin', 'directory'] }),
    ])
    qc.removeQueries({ queryKey: ['admin', 'user-detail', id] })
    void navigate('/admin/usuarios')
  }

  async function handlePasswordReset(): Promise<void> {
    if (!detail) return
    setBusy(true)
    try {
      await sendPasswordReset(detail.id)
      toast.ok('Correo enviado', { description: detail.email })
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo enviar el correo.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleReactivate(): Promise<void> {
    if (!detail) return
    setBusy(true)
    try {
      await reactivateUser(detail.id)
      toast.ok('Cuenta reactivada')
      await refreshDetail()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo reactivar la cuenta.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleRevokeSessions(): Promise<void> {
    if (!detail) return
    setBusy(true)
    try {
      const { revoked } = await revokeUserSessions(detail.id)
      toast.ok(`${revoked} sesiones cerradas`)
      await qc.invalidateQueries({ queryKey: ['admin', 'user-sessions', id] })
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudieron cerrar las sesiones.'))
    } finally {
      setBusy(false)
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-3xl py-10">
        <Card>
          <Card.Body>
            <p className="text-[12.5px] text-text-2">Cargando…</p>
          </Card.Body>
        </Card>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <Link to="/admin/usuarios" className="text-[12px] font-medium text-link hover:underline">
          ← Volver a usuarios
        </Link>
        <Card className="mt-4">
          <Card.Body>
            <p className="text-[13px] font-semibold text-text">No encontramos este usuario.</p>
            <p className="mt-1 text-[12.5px] text-text-2">
              Puede que haya sido eliminado o que el enlace sea incorrecto.
            </p>
            <div className="mt-3">
              <Button onClick={() => void navigate('/admin/usuarios')}>Volver a usuarios</Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  const isSelf = detail.id === selfId
  const sessions = sessionsList ?? []

  return (
    <div data-testid="admin-user-detail" className="mx-auto w-full max-w-3xl pb-16">
      <RoleModal
        target={roleTarget}
        onClose={() => setRoleTarget(null)}
        onSuccess={refreshDetail}
        roles={roles}
      />
      <ReasonModal
        target={reasonTarget}
        onClose={() => setReasonTarget(null)}
        onSuccess={refreshDetail}
      />
      <DeleteUserModal
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSuccess={afterDelete}
      />
      <SecondaryEmailModal
        target={secondaryTarget}
        onClose={() => setSecondaryTarget(null)}
        onSuccess={refreshDetail}
      />

      <Link to="/admin/usuarios" className="text-[12px] font-medium text-link hover:underline">
        ← Volver a usuarios
      </Link>

      <div className="mt-4 flex flex-wrap items-center gap-3.5">
        <Avatar name={detail.name} src={avatarSrc} size={54} hideName />
        <div className="min-w-0">
          <h1 className="text-[18px] font-extrabold leading-tight text-text">{detail.name}</h1>
          <p className="text-[12.5px] text-text-2">{detail.email}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {detail.platform_role !== null && (
              <Chip tone={ROLE_BADGE[detail.platform_role].tone}>
                {ROLE_BADGE[detail.platform_role].label}
              </Chip>
            )}
            <Chip tone={detail.active ? 'ok' : 'sens'} dot>
              {detail.active ? 'Activa' : 'Desactivada'}
            </Chip>
            <Chip tone={detail.mfa_enabled ? 'ok' : 'warn'} dot>
              {detail.mfa_enabled ? 'MFA activo' : 'Sin MFA'}
            </Chip>
          </div>
        </div>
      </div>

      <Card className="mt-5">
        <Card.Head title="Información" />
        <Card.Body>
          <InfoRow k="Correo">{detail.email}</InfoRow>
          {detail.secondary_email !== undefined && (
            <InfoRow k="Correo secundario">
              <span className="flex items-center gap-2">
                {detail.secondary_email ?? <span className="text-text-3">Sin registrar</span>}
                {detail.secondary_email !== null && (
                  <Chip tone={detail.secondary_email_status === 'verified' ? 'ok' : 'warn'} dot>
                    {detail.secondary_email_status === 'verified' ? 'Confirmado' : 'Pendiente'}
                  </Chip>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setSecondaryTarget({
                      id: detail.id,
                      name: detail.name,
                      secondary_email: detail.secondary_email ?? null,
                    })
                  }
                >
                  Editar
                </Button>
              </span>
            </InfoRow>
          )}
          <InfoRow k="Último acceso">
            <span className="flex flex-col items-end leading-tight">
              <span>{relativeDateEs(detail.last_login)}</span>
              {detail.last_login && (
                <span className="text-[10.5px] font-normal text-text-3">
                  {absoluteDateTimeEs(detail.last_login)}
                </span>
              )}
            </span>
          </InfoRow>
          <InfoRow k="Miembro desde">{absoluteDateTimeEs(detail.created_at)}</InfoRow>
          <InfoRow k="MFA enrolada">
            {mfaEnrolledLabel(detail.mfa_enabled, detail.mfa_enrolled_at)}
          </InfoRow>
        </Card.Body>
      </Card>

      {detail.tenants.length > 0 && (
        <Card className="mt-5">
          <Card.Head title="Clientes" />
          <Card.Body>
            <ul className="flex flex-col gap-2">
              {detail.tenants.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-2">
                  <Link to={`/admin/tenants/${t.id}`} className="text-link hover:underline">
                    {t.name}
                  </Link>
                  <span className="text-[11px] text-text-2">{t.role}</span>
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      <Card className="mt-5">
        <Card.Head
          title="Sesiones activas"
          action={
            !isSelf && sessions.length > 0 ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => void handleRevokeSessions()}
              >
                Cerrar todas sus sesiones
              </Button>
            ) : undefined
          }
        />
        <Card.Body>
          <DataTable
            caption="Sesiones activas"
            columns={sessionColumns}
            rows={sessions}
            loading={sessionsLoading}
            empty={
              <div className="px-5 py-8 text-center text-[12.5px] text-text-2">
                Sin sesiones activas.
              </div>
            }
          />
        </Card.Body>
      </Card>

      <Card className="mt-5">
        <Card.Head title="Acciones" />
        <Card.Body>
          <div className="flex flex-wrap items-center gap-2">
            {/* Role changes are staff-only (the server 409s not_platform_user
                otherwise) — a tenant user (platform_role null) never gets
                this button. */}
            {!isSelf && detail.platform_role !== null && (
              <Button
                variant="secondary"
                onClick={() =>
                  setRoleTarget({
                    id: detail.id,
                    name: detail.name,
                    platform_role: detail.platform_role!,
                  })
                }
              >
                Cambiar rol…
              </Button>
            )}
            {!isSelf && (
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => void handlePasswordReset()}
              >
                Enviar restablecimiento de contraseña
              </Button>
            )}
            {detail.mfa_enabled && (
              <Button
                variant="secondary"
                onClick={() => setReasonTarget({ kind: 'mfa-reset', id: detail.id })}
              >
                Restablecer MFA…
              </Button>
            )}
            {detail.active ? (
              !isSelf && (
                <Button
                  variant="destructive"
                  onClick={() => setReasonTarget({ kind: 'deactivate', id: detail.id })}
                >
                  Desactivar cuenta…
                </Button>
              )
            ) : (
              <Button variant="secondary" disabled={busy} onClick={() => void handleReactivate()}>
                Reactivar cuenta
              </Button>
            )}
            {!detail.active && !isSelf && session?.platformRole === 'platform_owner' && (
              <Button
                variant="destructive"
                onClick={() => setDeleteTarget({ id: detail.id, email: detail.email })}
              >
                Eliminar cuenta…
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}
